/**
 * Search parameters for auth redirects.
 */
export type AuthRedirectSearch = Readonly<{
  redirect?: string;
}>;

/**
 * Normalizes a redirect path to a safe internal route.
 *
 * @param value - Raw redirect value.
 *
 * @returns Normalized redirect path or undefined when invalid.
 */
export const normalizeRedirectPath = (
  value: string | null | undefined,
): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  if (!trimmed.startsWith('/') || isUnsafeRedirectPath(trimmed)) {
    return undefined;
  }

  try {
    const parsed = new URL(trimmed, 'https://chronock.invalid');
    return parsed.origin === 'https://chronock.invalid' ? trimmed : undefined;
  } catch (_error) {
    return undefined;
  }
};

/**
 * Checks raw and encoded path forms for browser URL delimiter ambiguity.
 *
 * @param path Candidate internal redirect path.
 *
 * @returns Whether the path can be interpreted as an external or invalid URL.
 */
function isUnsafeRedirectPath(path: string): boolean {
  let candidate = path;

  for (let depth = 0; depth < 8; depth += 1) {
    if (
      candidate.startsWith('//') ||
      candidate.includes('\\') ||
      containsControlCharacter(candidate)
    ) {
      return true;
    }

    try {
      const decoded = decodeURIComponent(candidate);
      if (decoded === candidate) {
        return false;
      }
      candidate = decoded;
    } catch (_error) {
      return true;
    }
  }

  return true;
}

/**
 * Checks for ASCII control characters that browsers may normalize in URLs.
 *
 * @param value Candidate path representation.
 *
 * @returns Whether an ASCII control character is present.
 */
function containsControlCharacter(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f)) {
      return true;
    }
  }

  return false;
}

/**
 * Builds search params for auth routes.
 *
 * @param redirectTo - Safe redirect path.
 *
 * @returns Search params for auth routes.
 */
export const buildAuthSearch = (redirectTo?: string): AuthRedirectSearch => {
  const normalizedRedirect = normalizeRedirectPath(redirectTo);

  return {
    ...(normalizedRedirect ? { redirect: normalizedRedirect } : {}),
  };
};

/**
 * Builds a full callback URL for auth flows.
 *
 * @param baseUrl - Application base URL.
 * @param path - Absolute path within the application.
 * @param search - Optional search parameters.
 *
 * @returns Full callback URL for the auth flow.
 */
export const buildAuthCallbackUrl = (
  baseUrl: string | undefined,
  path: string,
  search?: AuthRedirectSearch,
): string => {
  const normalizedBase = baseUrl?.replace(/\/$/, '') ?? '';
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const params = new URLSearchParams();
  const normalizedSearch = buildAuthSearch(search?.redirect);

  if (normalizedSearch.redirect) {
    params.set('redirect', normalizedSearch.redirect);
  }

  const query = params.toString();

  return query
    ? `${normalizedBase}${normalizedPath}?${query}`
    : `${normalizedBase}${normalizedPath}`;
};
